import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

// Constants
import { routes } from 'constants/routes'

// Hooks
import { useModalContext } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager'

// Components
import Link from 'components/Link'
import Section from 'components/Section'

// Modals
import Setup2FAModal from 'modules/user/modals/Setup2FAModal'
import Confirm2FADisabledModal from 'modules/user/modals/Confirm2FADisabledModal'
import RegenerateRecoveryCodesModal from 'modules/user/modals/RegenerateRecoveryCodesModal'

// Styles
import {
  Checkmark,
  Exclamation,
  Status,
  StyledButton,
  StatusList,
  StyledLink,
} from './TwoFactorAuthenticationPanel.styles'

// Types
import {
  InitialUserFragment,
  useConfirmDisableMfaMutation,
  useCancelDisableMfaMutation,
} from 'gqlTypes'

type LocationState = { MFADisabled?: boolean }

interface TwoFactorAuthenticationPanelProps {
  mfaState: InitialUserFragment['mfaState']
}

/**
 * Renders the Panel with the Two Factor Authentication controls
 * Allows the user to enable,disable and generate new codes the 2FA
 */
const TwoFactorAuthenticationPanel: React.FC<TwoFactorAuthenticationPanelProps> = ({
  mfaState,
}) => {
  const { showToast } = useToast()
  const history = useHistory()
  const search = useLocation<LocationState>().search
  const { showModal } = useModalContext()

  const [confirmDisableMfa, { loading }] = useConfirmDisableMfaMutation({
    onCompleted: () => {
      showModal(Confirm2FADisabledModal)
      history.replace(routes.PERSONAL_SETTINGS.create({}))
    },
    onError: ({ message }) => {
      showToast(message, 'negative')
    },
  })

  const [
    disableCancelMfa,
    { loading: loadingCancelDisableMfa },
  ] = useCancelDisableMfaMutation({
    onError: ({ message }) => {
      showToast(message, 'negative')
    },
  })

  // Trigger the "confirmDisableMfa" mutation.
  // This means the user initiated the disable mfa flow
  // and clicked the link in the email to confirm the disable
  useEffect(() => {
    const token = new URLSearchParams(search).get('token')

    if (token) {
      confirmDisableMfa({ variables: { token } })
    }
  }, [search, confirmDisableMfa])

  const renderMfaState = () => {
    if (mfaState === 'DISABLED') {
      return null
    }

    if (mfaState === 'ENABLED') {
      return (
        <>
          <Status status={mfaState}>
            <Checkmark /> 2FA Enabled
          </Status>
          <li>
            <StyledLink
              onClick={() => showModal(RegenerateRecoveryCodesModal)}
              isUnderlined
              external>
              Generate new recovery codes
            </StyledLink>
          </li>
        </>
      )
    }

    if (mfaState === 'PENDING_DISABLE') {
      return (
        <>
          <Status status={mfaState}>
            <Exclamation /> Awaiting confirmation from email to deactivate
          </Status>
          {/*
             TODO: Link to be decided
            Issue : https://github.com/sketch-hq/Cloud/issues/9939
        */}
          <li>
            <StyledLink isUnderlined external>
              I didn&apos;t receive an email
            </StyledLink>
          </li>
        </>
      )
    }
  }

  const handleMFA = () => {
    if (mfaState === 'DISABLED') {
      showModal(Setup2FAModal)
    }

    if (mfaState === 'ENABLED') {
      history.push(routes.PERSONAL_SETTINGS_DISABLE_MFA.create({}))
    }

    if (mfaState === 'PENDING_DISABLE') {
      disableCancelMfa()
    }
  }

  const getButtonCopy = () => {
    if (mfaState === 'ENABLED') {
      return 'Turn Off 2FA…'
    }

    if (mfaState === 'PENDING_DISABLE') {
      return 'Cancel'
    }

    return 'Set Up 2FA…'
  }

  const variant = mfaState === 'DISABLED' ? 'primary' : 'negative-secondary'

  return (
    <Section
      title="Two-Factor Authentication (2FA)"
      text={
        <div>
          Two-factor authentication protects your account by adding an extra
          security step when you sign in.{' '}
          {/*
        TODO: Add the link here when its available
        Issue: https://github.com/sketch-hq/Cloud/issues/9939
      */}
          <Link href="http://www.sketch.com" isUnderlined external>
            Learn more
          </Link>
        </div>
      }
      action={
        <StyledButton
          variant={variant}
          loading={loading || loadingCancelDisableMfa}
          onClick={handleMFA}
          small>
          {getButtonCopy()}
        </StyledButton>
      }>
      <StatusList>{renderMfaState()}</StatusList>
    </Section>
  )
}

export default TwoFactorAuthenticationPanel
